import { z } from "zod";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { EllipsisVerticalIcon, PlusIcon } from "lucide-react";
import {
  attributesValidator,
  rowKeysValidator,
  tableValidator,
} from "../../models/schemasV2";
import ColumnPopover from "./ColumnPopover";
import {
  HandleColumnActionParams,
  HandleRowKeyActionParams,
  HandleTableActionParams,
  HandleTableOrderActionParams,
} from "../../hooks/templateV2/types";
import { TablePopover } from "./TablePopover";
import { RowKeyPopover } from "./RowKeyPopover";
import { useMemo, useState, useRef, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

interface TemplateTableProps {
  table: z.infer<typeof tableValidator>;
  handleColumnAction: (data: HandleColumnActionParams) => void;
  handleDeleteTable: (tableId: string) => void;
  handleTableAction: (params: HandleTableActionParams) => void;
  handleRowKeyAction: (params: HandleRowKeyActionParams) => void;
  handleTableOrderAction: (params: HandleTableOrderActionParams) => void;
}

const DraggableTableHead = ({
  attribute,
  index,
  moveAttribute,
  handleColumnAction,
  tableId,
}: {
  attribute: z.infer<typeof attributesValidator>;
  index: number;
  moveAttribute: (startIndex: number, endIndex: number) => void;
  handleColumnAction: (data: HandleColumnActionParams) => void;
  tableId: string;
}) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "COLUMN",
    item: { id: attribute.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "COLUMN",
    hover(item: { id: string; index: number }) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      item.index = dragIndex;
    },
    drop(item: { id: string; index: number }) {
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex !== hoverIndex) {
        moveAttribute(dragIndex, hoverIndex);
      }
    },
  });

  drag(drop(ref));

  return (
    <TableHead
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className="border border-stone-200 hover:bg-stone-300 cursor-move w-32 max-w-32 truncate"
    >
      <Popover>
        <PopoverTrigger asChild>
          <div>{attribute.name}</div>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[500px]">
          <ColumnPopover
            onSave={(data) => handleColumnAction({ ...data, tableId: tableId })}
            tableId={tableId}
            column={attribute}
          />
        </PopoverContent>
      </Popover>
    </TableHead>
  );
};

const TemplateTable = ({
  table,
  handleDeleteTable,
  handleColumnAction,
  handleTableAction,
  handleRowKeyAction,
  handleTableOrderAction,
}: TemplateTableProps) => {
  // TODO: Fix the issue where the popover is filled with the previous row key data
  const [isAddRowKeyPopoverOpen, setAddRowKeyPopoverOpen] = useState(false);

  const rowKeyAttribute = useMemo(() => {
    return table.attributes.find(
      (attribute) => attribute.id === table.rowKeyColumnId,
    );
  }, [table.attributes, table.rowKeyColumnId]);

  const sortedAttributes = useMemo(() => {
    return table.attributes
      .filter((attribute) => attribute.id !== table.rowKeyColumnId)
      .sort((a, b) => (a.attributeOrder ?? 0) - (b.attributeOrder ?? 0));
  }, [table.attributes, table.rowKeyColumnId]);

  const moveAttribute = useCallback(
    (startIndex: number, endIndex: number) => {
      const newOrder = Array.from(sortedAttributes);
      const [reorderedItem] = newOrder.splice(startIndex, 1);
      newOrder.splice(endIndex, 0, reorderedItem);

      const newAttributesOrder = newOrder.map((attr) => attr.id);
      handleTableOrderAction({
        tableId: table.id,
        attributesOrder: newAttributesOrder,
      });
    },
    [sortedAttributes, handleTableOrderAction, table.id],
  );

  return (
    <div className="my-4 p-4 rounded-sm border border-stone-200">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">{table.name}</h3>

        <div className="flex items-center">
          <Popover>
            <PopoverTrigger asChild className="cursor-pointer">
              <EllipsisVerticalIcon size={16} />
            </PopoverTrigger>
            <TablePopover
              table={table}
              deleteTable={handleDeleteTable}
              updateTable={handleTableAction}
            />
          </Popover>
        </div>
      </div>
      {/*<div className="text-font-secondary">{table.description}</div>*/}
      <div className="flex mt-4">
        <DndProvider backend={HTML5Backend}>
          <Table className="w-auto">
            <TableHeader className="bg-stone-50">
              <TableRow>
                {rowKeyAttribute && (
                  <TableHead className="border border-stone-200">
                    {rowKeyAttribute.name}
                  </TableHead>
                )}
                {sortedAttributes.map((attribute, index) => (
                  <DraggableTableHead
                    key={attribute.id}
                    attribute={attribute}
                    index={index}
                    moveAttribute={moveAttribute}
                    handleColumnAction={handleColumnAction}
                    tableId={table.id}
                  />
                ))}
                <Popover>
                  <PopoverTrigger asChild>
                    <TableHead className="bg-white text-stone-950 border border-stone-200 hover:bg-stone-200 cursor-pointer">
                      <PlusIcon />
                    </TableHead>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[500px]">
                    <ColumnPopover
                      onSave={(data) =>
                        handleColumnAction({ ...data, tableId: table.id })
                      }
                      columnOrder={sortedAttributes.length}
                      tableId={table.id}
                    />
                  </PopoverContent>
                </Popover>
              </TableRow>
            </TableHeader>
            {rowKeyAttribute && (
              <TableBody>
                {table.rowKeys &&
                  table.rowKeys.map(
                    (rowKey: z.infer<typeof rowKeysValidator>) => (
                      <TableRow key={rowKey.id}>
                        <Popover>
                          <PopoverTrigger asChild>
                            <TableCell className="border border-stone-200 w-32 max-w-32 truncate cursor-pointer hover:bg-stone-200">
                              {rowKey.name}
                            </TableCell>
                          </PopoverTrigger>
                          <RowKeyPopover
                            rowKey={rowKey}
                            tableId={table.id}
                            handleRowKeyAction={handleRowKeyAction}
                          />
                        </Popover>
                      </TableRow>
                    ),
                  )}
                <TableRow>
                  <Popover
                    open={isAddRowKeyPopoverOpen}
                    onOpenChange={setAddRowKeyPopoverOpen}
                  >
                    <PopoverTrigger asChild>
                      <TableCell
                        className="flex items-center justify-center bg-white text-stone-950 border-x border-b border-stone-200 cursor-pointer hover:bg-stone-200"
                        onClick={() => setAddRowKeyPopoverOpen(true)}
                      >
                        <PlusIcon />
                      </TableCell>
                    </PopoverTrigger>
                    {isAddRowKeyPopoverOpen && (
                      <RowKeyPopover
                        tableId={table.id}
                        handleRowKeyAction={handleRowKeyAction}
                      />
                    )}
                  </Popover>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </DndProvider>
      </div>
    </div>
  );
};

export default TemplateTable;
